// OWNED BY the meetings implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/meetings/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// פגישות tab: the contact's calendar MEETINGS (every non-task event type) from
// the user's own calendar (calendar-settings-v2), grouped הבאות (soonest first,
// today's highlighted) / עבר (newest first, collapsed beyond 10). Row click
// opens the global calendar event dialog for that event (owner-only);
// "קביעת פגישה" opens the same dialog prefilled with this contact.
//
// Data flow (mirrors the tasks tab's contact-scoped approach WITHOUT importing
// from tabs/tasks/ — see tabs/meetings/useContactMeetings.ts): CalendarContext
// live onSnapshot + session-cache warmed fallback, so the tab renders instantly
// on a warm open. Cold-click race avoided: before the live events land, a row
// click opens the editor directly on the cached event copy (full event incl.
// id → CalendarForm opens in edit mode) instead of the tasks tab's silent no-op.

import { useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import EmptyContent from "components/EmptyContent";
import useCalendar from "hooks/useCalendar";
import { parseContactsToAttendees } from "utils/calendar/helper";
import { TabCard } from "../TabCard";
import { useContactMeetings } from "./meetings/useContactMeetings";
import { ContactMeetingRow } from "./meetings/ContactMeetingRow";
import { warmContactMeetingsCache } from "./meetings/meetingsData";
import type { CalendarEvent, RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

const PAST_COLLAPSED_COUNT = 10;

export default function MeetingsTab({ contact }: ContactTabComponentProps): JSX.Element {
  const { showSingleEventEditorModal, showSingleEventEditorModalByType } = useCalendar();

  const { loading, live, upcoming, past, todayCount } = useContactMeetings(contact);

  const [showAllPast, setShowAllPast] = useState(false);

  const handleNewMeeting = (): void => {
    // Same prefill convention as the page header / contacts-table row menu.
    showSingleEventEditorModalByType?.("meeting", {
      attendees: parseContactsToAttendees([contact])
    });
  };

  const openMeetingEditor = (meeting: CalendarEvent): void => {
    if (!meeting.id) return;

    if (live) {
      // Canonical path: the dialog resolves the freshest copy from the live events.
      showSingleEventEditorModal(meeting.id);
      return;
    }

    // Cold-click fallback (the tasks tab's known race, avoided here): live
    // events haven't landed yet, so open the dialog directly on the cached
    // copy — passing the FULL event (id included) puts CalendarForm in edit
    // mode and saves through the same createOrUpdateEvent path.
    showSingleEventEditorModalByType?.(meeting.eventType === "task" ? "task" : "meeting", meeting);
  };

  const hasAnyMeeting = upcoming.length > 0 || past.length > 0;
  const visiblePast = showAllPast ? past : past.slice(0, PAST_COLLAPSED_COUNT);
  const hiddenPastCount = past.length - visiblePast.length;

  return (
    <Stack gap={2.5} sx={{ maxWidth: 880, mx: "auto", width: "100%" }}>
      <TabCard>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="baseline" gap={1} sx={{ minWidth: 0 }}>
            <Typography variant="h6">פגישות</Typography>
            {!loading && (
              <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                {upcoming.length} מתוכננות
                {todayCount > 0 ? ` · ${todayCount} היום` : ""}
              </Typography>
            )}
          </Stack>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={handleNewMeeting}>
            קביעת פגישה
          </Button>
        </Stack>

        {loading ? (
          <MeetingsSkeleton />
        ) : !hasAnyMeeting ? (
          <EmptyContent
            title="אין פגישות עם הלקוח"
            description="קבעו פגישה ראשונה — היא תופיע גם ביומן וגם כאן."
          />
        ) : (
          <>
            <MeetingsGroupHeader label="הבאות" count={upcoming.length} />
            {upcoming.length ? (
              upcoming.map((meeting) => (
                <ContactMeetingRow
                  key={meeting.id ?? `${meeting.title}-${String(meeting.start)}`}
                  meeting={meeting}
                  contact={contact}
                  isUpcoming
                  onOpen={openMeetingEditor}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary", px: 1, py: 0.75 }}>
                אין פגישות מתוכננות
              </Typography>
            )}

            {past.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <MeetingsGroupHeader label="עבר" count={past.length} />
                {visiblePast.map((meeting) => (
                  <ContactMeetingRow
                    key={meeting.id ?? `${meeting.title}-${String(meeting.start)}`}
                    meeting={meeting}
                    contact={contact}
                    isUpcoming={false}
                    onOpen={openMeetingEditor}
                  />
                ))}
                {hiddenPastCount > 0 && (
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowAllPast(true)}>
                    הצגת עוד {hiddenPastCount} פגישות עבר
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
      </TabCard>
    </Stack>
  );
}

function MeetingsGroupHeader({ label, count }: { label: string; count: number }): JSX.Element {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        color: "text.secondary",
        mb: 0.5,
        pb: 0.5,
        borderBottom: (theme) => `1px dashed ${theme.palette.divider}`
      }}
    >
      {label} · {count}
    </Typography>
  );
}

function MeetingsSkeleton(): JSX.Element {
  return (
    <Stack gap={1.5} sx={{ mt: 1 }}>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" alignItems="center" gap={1.5}>
          <Skeleton variant="circular" width={20} height={20} />
          <Stack sx={{ flexGrow: 1 }}>
            <Skeleton width="45%" height={20} />
            <Skeleton width="25%" height={16} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: count of upcoming meetings for this contact; turns
// info-colored when one of them is today. Reads only from CalendarContext's
// onSnapshot state / the session cache — no per-render fetching.
export function useMeetingsTabBadge(contact: RealEstateContact): TabBadge {
  const { upcoming, todayCount } = useContactMeetings(contact);

  if (!upcoming.length) return null;

  return { count: upcoming.length, color: todayCount > 0 ? "info" : "default" };
}

// Cache warm-up fired on contacts-table row hover and page mount: one-shot
// fetch of the user's own calendar into the session cache (same shared key as
// the tasks tab, so hovering warms both tabs with a single fetch). Never throws.
export function preloadMeetingsTab(_contact: RealEstateContact): void {
  try {
    warmContactMeetingsCache();
  } catch {
    // preloads are best-effort by contract
  }
}
